import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, LayersControl, LayerGroup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SolarHeatmap from './SolarHeatmap';
import SolarLegend from './SolarLegend';
import MapLegend from './MapLegend';
import WindLayer from './WindLayer';

import windData from '../data/wind-global.json';

const OCEAN_WIND_SCALE = ['#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7', '#0369a1'];
const CLASSIC_WIND_SCALE = ['#4a90e2', '#50e3c2', '#b8e986', '#f8e71c', '#f5a623', '#d0021b'];
const WHITE_WIND_SCALE = ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];

// ── Custom Zoom Slider (with inline Wind Toggle) ────────────────────────────
const CustomZoomSlider = ({ showWind, setShowWind, windColorScale, setWindColorScale, isEmbedded }) => {
    const map = useMap();
    const [zoom, setZoom] = React.useState(map.getZoom());
    const containerRef = React.useRef(null);

    useEffect(() => {
        const onZoom = () => setZoom(map.getZoom());
        map.on('zoom', onZoom);
        return () => map.off('zoom', onZoom);
    }, [map]);

    // ── Stop Leaflet from capturing mouse/touch events from this overlay ──
    // React's synthetic stopPropagation alone doesn't block Leaflet's native
    // DomEvent handlers. L.DomEvent.disableClickPropagation is the only
    // reliable fix — it cancels the event at the native DOM level.
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !L.DomEvent) return;
        L.DomEvent.disableClickPropagation(el);
        L.DomEvent.disableScrollPropagation(el);
    }, []);

    const minZ = 2;
    const maxZ = 18;
    const percentage = Math.round(((Math.max(minZ, Math.min(maxZ, zoom)) - minZ) / (maxZ - minZ)) * 100);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                bottom: 30,
                right: 15,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                borderRadius: 20,
                overflow: 'hidden',
            }}
        >
            {/* ── Wind Toggle + Thickness ── */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px 6px 12px',
                    background: showWind ? 'rgba(30,40,60,0.88)' : '#ffffff',
                    transition: 'background 0.22s ease',
                    borderRight: '1px solid rgba(0,0,0,0.08)',
                    userSelect: 'none',
                }}
            >
                {/* Clickable wind label+toggle */}
                <div
                    onClick={() => setShowWind(w => !w)}
                    title={showWind ? 'Hide wind direction' : 'Show wind direction'}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                >
                    <span style={{ fontSize: 13, lineHeight: 1 }}>💨</span>
                    <span style={{
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: 'Inter, sans-serif',
                        color: showWind ? 'rgba(200,230,255,0.95)' : '#374151',
                        letterSpacing: '0.03em',
                        whiteSpace: 'nowrap',
                    }}>Wind</span>
                </div>
                {/* Toggle pill */}
                <div
                    onClick={() => setShowWind(w => !w)}
                    style={{
                        width: 30,
                        height: 16,
                        borderRadius: 8,
                        background: showWind ? '#60b4ff' : '#D1D5DB',
                        position: 'relative',
                        transition: 'background 0.22s ease',
                        flexShrink: 0,
                        cursor: 'pointer',
                    }}
                >
                    <div style={{
                        position: 'absolute',
                        top: 2,
                        left: showWind ? 14 : 2,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                        transition: 'left 0.22s ease',
                    }} />
                </div>

                {/* Color Selector — only visible when wind is ON */}
                {showWind && (
                    <>
                        <div style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                        <select
                            value={windColorScale}
                            onChange={e => setWindColorScale(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            style={{
                                fontSize: 11,
                                padding: '2px 4px',
                                borderRadius: 4,
                                border: '1px solid #d1d5db',
                                background: '#fff',
                                color: '#374151',
                                outline: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="classic">Classic</option>
                            <option value="ocean">Ocean</option>
                            <option value="white">White</option>
                        </select>
                    </>
                )}
            </div>

            {/* ── Zoom Controls ── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                background: '#ffffff',
            }}>
                <button onClick={() => map.setZoom(Math.max(minZ, zoom - 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '500', color: '#374151', padding: '0 2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                <input
                    type="range"
                    min={minZ}
                    max={maxZ}
                    step="any"
                    value={zoom}
                    onChange={(e) => map.setZoom(Number(e.target.value))}
                    style={{ width: '55px', margin: 0, accentColor: '#76AB3F', cursor: 'pointer' }}
                />
                <button onClick={() => map.setZoom(Math.min(maxZ, zoom + 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '500', color: '#374151', padding: '0 2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                <span style={{ fontSize: '11px', fontWeight: '600', color: '#374151', minWidth: '24px', textAlign: 'right', fontFamily: 'Inter, sans-serif' }}>
                    {percentage}%
                </span>
            </div>
        </div>
    );
};

// ── Solar config ─────────────────────────────────────────────────────────────
const CATEGORY_STYLES = {
    Gold: { color: '#F59E0B', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.3)', label: '☀️ Gold', tagBg: 'rgba(245,158,11,0.2)', tagText: '#F59E0B' },
    Silver: { color: '#60A5FA', bg: 'rgba(96,165,250,0.15)', border: 'rgba(96,165,250,0.3)', label: '💧 Silver', tagBg: 'rgba(96,165,250,0.2)', tagText: '#60A5FA' },
    Moderate: { color: '#FB923C', bg: 'rgba(251,146,60,0.15)', border: 'rgba(251,146,60,0.3)', label: '⚡ Moderate', tagBg: 'rgba(251,146,60,0.2)', tagText: '#FB923C' },
    Poor: { color: '#EF4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', label: '⚠️ Poor', tagBg: 'rgba(239,68,68,0.2)', tagText: '#EF4444' },
    Unknown: { color: '#6B7280', bg: 'rgba(107,114,128,0.15)', border: 'rgba(107,114,128,0.3)', label: '❓ Unknown', tagBg: 'rgba(107,114,128,0.2)', tagText: '#6B7280' },
};

// ── Color scaling for new layers ─────────────────────────────────────────────
const getIrradianceColor = (value) => {
    if (value == null) return '#6B7280';
    if (value >= 1000) return '#FEF9C3';
    if (value >= 800) return '#FEF08A';
    if (value >= 600) return '#FDE047';
    if (value >= 400) return '#F59E0B';
    if (value >= 200) return '#D97706';
    if (value >= 50) return '#92400E';
    return '#1E3A8A';
};

const getTemperatureColor = (value) => {
    if (value == null) return '#6B7280';
    if (value >= 30) return '#991B1B';
    if (value >= 25) return '#DC2626';
    if (value >= 20) return '#EA580C';
    if (value >= 15) return '#F59E0B';
    if (value >= 10) return '#10B981';
    if (value >= 5) return '#06B6D4';
    if (value >= 0) return '#3B82F6';
    return '#1E3A8A';
};

const getWindColor = (speed) => {
    if (speed == null) return '#6B7280';
    if (speed >= 90) return '#2a0001';
    if (speed >= 80) return '#72222f';
    if (speed >= 70) return '#b02f6b';
    if (speed >= 60) return '#ce4c58';
    if (speed >= 50) return '#d88e36';
    if (speed >= 45) return '#e9bc26';
    if (speed >= 40) return '#dcdb31';
    if (speed >= 35) return '#bade3f';
    if (speed >= 30) return '#83cc4b';
    if (speed >= 25) return '#31b94d';
    if (speed >= 20) return '#41adb5';
    if (speed >= 15) return '#478ec7';
    if (speed >= 10) return '#476fbb';
    if (speed >= 5) return '#5651a2';
    return '#605e71';
};

const getWindGustColor = (speed) => {
    if (speed == null) return '#6B7280';
    if (speed >= 100) return '#1a0000';
    if (speed >= 90) return '#2a0001';
    if (speed >= 80) return '#72222f';
    if (speed >= 70) return '#b02f6b';
    if (speed >= 60) return '#ce4c58';
    if (speed >= 50) return '#d88e36';
    if (speed >= 45) return '#e9bc26';
    if (speed >= 40) return '#dcdb31';
    if (speed >= 35) return '#bade3f';
    if (speed >= 30) return '#83cc4b';
    if (speed >= 25) return '#31b94d';
    if (speed >= 20) return '#41adb5';
    if (speed >= 15) return '#478ec7';
    if (speed >= 10) return '#476fbb';
    if (speed >= 5) return '#5651a2';
    return '#605e71';
};

const getCloudColor = (cover) => {
    if (cover == null) return '#6B7280';
    if (cover >= 80) return '#374151';
    if (cover >= 50) return '#6B7280';
    if (cover >= 20) return '#D1D5DB';
    return '#F3F4F6';
};

const getThunderstormColor = (cape) => {
    if (cape == null) return '#6B7280';
    if (cape >= 5000) return '#270104';
    if (cape >= 4000) return '#571330';
    if (cape >= 3000) return '#7a1945';
    if (cape >= 2500) return '#9e225e';
    if (cape >= 2000) return '#db5858';
    if (cape >= 1600) return '#eab308';
    if (cape >= 1200) return '#b2d94a';
    if (cape >= 800) return '#4cb490';
    if (cape >= 400) return '#425da6';
    if (cape >= 200) return '#5b57a3';
    return '#8c8c8c';
};

// ── Time offset helper ──────────────────────────────────────────────────────
const getTargetSec = (timeOffset = 0) => Math.floor(Date.now() / 1000) + (timeOffset * 3600);

// ── Formatting helpers ──────────────────────────────────────────────────────
const formatTemp = (c) => c != null && !isNaN(c) ? Math.round((Number(c) * 9) / 5 + 32) : '--';
const formatWind = (k) => k != null && !isNaN(k) ? (Number(k) * 0.621371).toFixed(1) : '0';


// ── D-1 Scoring ─────────────────────────────────────────────────────────────
const calculateD1Score = (forecast24h, spaceWeather) => {
    if (!forecast24h || forecast24h.length === 0) return null;
    let totalIrradiance = 0;
    let totalTemp = 0;
    const hours = forecast24h.slice(0, 24);
    hours.forEach(h => {
        totalIrradiance += (h.ghi || 0);
        totalTemp += (h.temperature || 0);
    });
    const avgIrradiance = totalIrradiance / hours.length;
    const avgTemp = totalTemp / hours.length;
    const stormRisk = spaceWeather?.stormRisk || 0;

    // Normalised Irradiance Score (0-100) -> Cap at 400 W/m² -> 100
    const irradScore = Math.min(100, (avgIrradiance / 300) * 100);
    const tempDerating = avgTemp > 25 ? (avgTemp - 25) * 0.4 : 0;

    let finalScore = irradScore - tempDerating - (stormRisk * 0.5);
    finalScore = Math.max(0, Math.min(100, Math.round(finalScore)));

    let color = '#10B981'; // Green
    if (finalScore < 40) color = '#EF4444'; // Red
    else if (finalScore < 75) color = '#F59E0B'; // Yellow

    let advice = 'Clear skies, minimal interference.';
    if (stormRisk > 40) advice = 'Warning: Expected solar flaring could cause radio/electrical interference.';
    else if (avgIrradiance < 100) advice = 'Low irradiance expected (Heavy clouds).';
    else if (avgTemp > 35) advice = 'High temperatures may cause hardware throttling.';

    return { score: finalScore, color, advice, avgIrradiance: Math.round(avgIrradiance), avgTemp: Math.round(avgTemp) };
};

// ── Content builders ──────────────────────────────────────────────────────────
// buildTooltipContent: lightweight summary for hover
const buildTooltipContent = (siteName, clientName, lat, lng, site, viewMode, solar, forecast, spaceWeather, timeOffset = 0) => {
    let metricHtml = '';
    let accent = '#76AB3F';

    if (viewMode !== 'client') {
        if (solar) {
            if (viewMode === 'score') {
                const cat = CATEGORY_STYLES[solar.category] || CATEGORY_STYLES.Unknown;
                accent = cat.color;
                metricHtml = `
                    <div style="margin-bottom:8px;padding:6px;background:${cat.bg};border-radius:6px;text-align:center;">
                        <span style="font-size:12px;font-weight:700;color:${cat.tagText};">${cat.label} (Irradiance: ${solar.score} kWh/m²)</span>
                        <div style="margin-top:6px;height:4px;background:rgba(0,0,0,0.06);border-radius:2px;overflow:hidden;">
                            <div style="width:${Math.min(100, (solar.score / 8) * 100)}%;height:100%;background:${cat.color};border-radius:2px;"></div>
                        </div>
                    </div>`;
            }
        }

        if (forecast) {
            if (viewMode === 'forecast') {
                const d1 = calculateD1Score(forecast, spaceWeather);
                if (d1) {
                    accent = d1.color;
                    metricHtml = `
                        <div style="margin-bottom:8px;padding:6px;background:${d1.color}22;border-radius:6px;text-align:center;border:1px solid ${d1.color}66;">
                            <span style="font-size:12px;font-weight:700;color:#111827;">D-1 Forecast Score: ${d1.score}/100</span>
                        </div>`;
                }
            } else if (viewMode !== 'client' && viewMode !== 'score') {
                const targetSec = getTargetSec(timeOffset);
                let fc = forecast.find(f => (typeof f.time === 'number' ? f.time : Math.floor(new Date(f.time).getTime() / 1000)) >= targetSec);
                if (!fc && forecast.length > 1) fc = forecast[1];
                if (fc) {
                    if (viewMode === 'wind') {
                        accent = getWindColor(fc.windSpeed);
                        metricHtml = `
                            <div style="margin-bottom:8px;padding:6px;background:${accent}22;border-radius:6px;text-align:center;border:1px solid ${accent}66;">
                                <span style="font-size:12px;font-weight:700;color:#111827;">💨 Wind Speed: ${formatWind(fc.windSpeed)} mph</span>
                            </div>`;
                    } else if (viewMode === 'wind_gust') {
                        accent = getWindGustColor(fc.windGust);
                        metricHtml = `
                            <div style="margin-bottom:8px;padding:6px;background:${accent}22;border-radius:6px;text-align:center;border:1px solid ${accent}66;">
                                <span style="font-size:12px;font-weight:700;color:#111827;">🌪️ Wind Gust: ${formatWind(fc.windGust)} mph</span>
                            </div>`;
                    } else if (viewMode === 'cloud') {
                        accent = '#4B5563';
                        metricHtml = `
                            <div style="margin-bottom:8px;padding:6px;background:#F3F4F6;border-radius:6px;text-align:center;border:1px solid #D1D5DB;">
                                <span style="font-size:12px;font-weight:700;color:#111827;">☁️ Cloud Cover: ${fc.cloudCover || 0}%</span>
                            </div>`;
                    } else if (viewMode === 'thunderstorm') {
                        accent = getThunderstormColor(fc.cape);
                        metricHtml = `
                            <div style="margin-bottom:8px;padding:6px;background:${accent}22;border-radius:6px;text-align:center;border:1px solid ${accent}66;">
                                <span style="font-size:12px;font-weight:700;color:#111827;">🌩️ Thunderstorm CAPE: ${fc.cape || 0} J/kg</span>
                            </div>`;
                    } else if (viewMode === 'irradiance') {
                        accent = getIrradianceColor(fc.ghi);
                        metricHtml = `
                            <div style="margin-bottom:8px;padding:6px;background:${accent}22;border-radius:6px;text-align:center;border:1px solid ${accent}66;">
                                <span style="font-size:12px;font-weight:700;color:#111827;">☀️ GHI (Irradiance): ${fc.ghi || 0} W/m²</span>
                            </div>`;
                    } else if (viewMode === 'temperature') {
                        accent = getTemperatureColor(fc.temperature);
                        metricHtml = `
                            <div style="margin-bottom:8px;padding:6px;background:${accent}22;border-radius:6px;text-align:center;border:1px solid ${accent}66;">
                                <span style="font-size:12px;font-weight:700;color:#111827;">🌡️ Temperature: ${formatTemp(fc.temperature)}°F</span>
                            </div>`;
                    }
                }
            }
        } else if (viewMode !== 'client' && viewMode !== 'score') {
            // Placeholder if forecast isn't loaded yet
            metricHtml = `
                <div style="margin-bottom:8px;padding:6px;background:#F3F4F6;border-radius:6px;text-align:center;border:1px solid #E5E7EB;">
                    <span style="font-size:11px;font-style:italic;color:#6B7280;">Click pin to load live forecast...</span>
                </div>`;
        }
    }

    return `
        <div style="font-family:'Inter',sans-serif;min-width:200px;overflow:hidden;border-radius:12px;background:#fff;">
            <div style="height:3px;background:linear-gradient(90deg,${accent},${accent}44);"></div>
            <div style="padding:10px 14px;">
                <div style="font-size:13px;font-weight:700;color:#1a2e0a;margin-bottom:6px;line-height:1.3;word-break:break-word;">
                    ${siteName}
                </div>
                ${metricHtml}
                <div style="display:inline-flex;align-items:center;gap:5px;background:#EAF4DB;border:1px solid #C7E0A8;border-radius:20px;padding:2px 9px;margin-bottom:8px;">
                    <div style="width:5px;height:5px;border-radius:50%;background:#76AB3F;"></div>
                    <span style="font-size:10px;font-weight:600;color:#4a7a28;letter-spacing:0.04em;">${clientName}</span>
                </div>
                <div style="display:flex;flex-direction:column;gap:4px;">
                    <div style="display:flex;align-items:center;justify-content:space-between;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">Coordinates</span>
                        <span style="font-size:11px;color:#374151;font-family:monospace;font-weight:600;">${lat.toFixed(4)}, ${lng.toFixed(4)}</span>
                    </div>
                    ${site.address ? `
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;flex-shrink:0;">Address</span>
                        <span style="font-size:11px;color:#374151;text-align:right;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px;">${site.address}</span>
                    </div>` : ''}
                    ${site.postal_code ? `
                    <div style="display:flex;align-items:center;justify-content:space-between;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">ZIP</span>
                        <span style="font-size:11px;color:#374151;">${site.postal_code}</span>
                    </div>` : ''}
                </div>
                <div style="margin-top:8px;padding-top:6px;border-top:1px solid #E5EDDB;text-align:center;">
                    <span style="font-size:10px;color:#9ca3af;font-weight:500;font-style:italic;">Click for detailed forecast →</span>
                </div>
            </div>
        </div>
    `;
};

// buildPopupContent: full detail card for click  
const buildPopupContent = (siteName, clientName, lat, lng, site, viewMode, solar, forecast, spaceWeather, timeOffset = 0) => {
    const catStyle = CATEGORY_STYLES[solar?.category] || CATEGORY_STYLES.Unknown;
    let accentColor = viewMode !== 'client' && solar ? catStyle.color : '#76AB3F';

    // Build forecast section
    let forecastSection = '';
    if (forecast && forecast.length > 0 && forecast[0].error) {
        forecastSection = `
            <div style="margin-top:12px;padding-top:12px;border-top:1px solid #E5EDDB;">
                <div style="display:flex;align-items:center;gap:6px;">
                    <span style="font-size:11px;color:#ef4444;font-style:italic;">⚠️ Forecast temporarily unavailable (API limits reached).</span>
                </div>
            </div>`;
    } else if (forecast && forecast.length > 1) {
        // Find the next hour forecast (handle both new unixtime and legacy ISO time caches)
        const targetSec = getTargetSec(timeOffset);
        let fc = forecast.find(f => {
            if (typeof f.time === 'number') return f.time >= targetSec;
            return Math.floor(new Date(f.time).getTime() / 1000) >= targetSec;
        });
        if (!fc) fc = forecast[1]; // fallback
        const fcTime = new Date((typeof fc.time === 'number' ? fc.time * 1000 : new Date(fc.time).getTime()));
        const fcH = fcTime.getHours();
        const fcAmpm = fcH >= 12 ? 'PM' : 'AM';
        const fcH12 = fcH % 12 || 12;
        const fcMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const fcTimeLabel = timeOffset === 0
            ? '(Next Hour)'
            : `(${fcMonths[fcTime.getMonth()]} ${fcTime.getDate()}, ${fcH12}:00 ${fcAmpm})`;

        forecastSection = `
            <div style="margin-top:12px;padding-top:12px;border-top:1px solid #E5EDDB;">
                <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
                    <span style="font-size:11px;font-weight:700;color:#2563EB;letter-spacing:0.04em;">⚡ ${timeOffset === 0 ? 'LIVE FORECAST' : 'WEATHER DATA'}</span>
                    <span style="font-size:9px;color:#9ca3af;font-weight:500;">${fcTimeLabel}</span>
                </div>
                <div style="display:flex;flex-direction:column;gap:4px;">
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">🌡️ Temperature</span>
                        <span style="font-size:11px;color:#C2410C;font-weight:700;">${formatTemp(fc.temperature)}°F</span>
                    </div>
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">☁️ Cloud Cover</span>
                        <span style="font-size:11px;color:#4B5563;font-weight:700;">${fc.cloudCover}%</span>
                    </div>
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">☀️ GHI (Irradiance)</span>
                        <span style="font-size:11px;color:#D97706;font-weight:700;">${fc.ghi} W/m²</span>
                    </div>
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">💨 Wind Speed</span>
                        <span style="font-size:11px;color:#0369a1;font-weight:700;">${formatWind(fc.windSpeed)} mph</span>
                    </div>
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">🌪️ Wind Gust</span>
                        <span style="font-size:11px;color:#1d4ed8;font-weight:700;">${formatWind(fc.windGust)} mph</span>
                    </div>
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">🌩️ Thunderstorm CAPE</span>
                        <span style="font-size:11px;color:${(fc.cape || 0) > 1000 ? '#B91C1C' : '#059669'};font-weight:700;">
                            ${fc.cape || 0} J/kg
                        </span>
                    </div>
                    ${fc.dni != null ? `
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">🎯 DNI (Direct)</span>
                        <span style="font-size:11px;color:#7C3AED;font-weight:700;">${fc.dni} W/m²</span>
                    </div>` : ''}
                </div>
            </div>`;
    } else {
        forecastSection = `
            <div style="margin-top:12px;padding-top:12px;border-top:1px solid #E5EDDB;">
                <div style="display:flex;align-items:center;gap:6px;">
                    <div style="width:12px;height:12px;border:2px solid #9ca3af;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;"></div>
                    <span style="font-size:11px;color:#9ca3af;font-style:italic;">Loading live forecast...</span>
                </div>
            </div>`;
    }

    return `
        <div style="font-family:'Inter',sans-serif;min-width:250px;max-width:300px;overflow:hidden;border-radius:16px;background:#fff;">
            <div style="height:3px;background:linear-gradient(90deg,${accentColor},#2563EB);"></div>
            <div style="padding:14px 16px;">
                <div style="font-size:14px;font-weight:700;color:#1a2e0a;margin-bottom:8px;line-height:1.3;word-break:break-word;">
                    ${siteName}
                </div>
                <div style="display:inline-flex;align-items:center;gap:5px;background:#EAF4DB;border:1px solid #C7E0A8;border-radius:20px;padding:2px 9px;margin-bottom:12px;">
                    <div style="width:5px;height:5px;border-radius:50%;background:#76AB3F;"></div>
                    <span style="font-size:10px;font-weight:600;color:#4a7a28;letter-spacing:0.04em;">${clientName}</span>
                </div>
                <div style="display:flex;flex-direction:column;gap:5px;">
                    <div style="display:flex;align-items:center;justify-content:space-between;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">Coordinates</span>
                        <span style="font-size:11px;color:#374151;font-family:monospace;font-weight:600;">${lat.toFixed(4)}, ${lng.toFixed(4)}</span>
                    </div>
                    ${site.address ? `
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;flex-shrink:0;">Address</span>
                        <span style="font-size:11px;color:#374151;text-align:right;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px;">${site.address}</span>
                    </div>` : ''}
                    ${site.postal_code ? `
                    <div style="display:flex;align-items:center;justify-content:space-between;">
                        <span style="font-size:11px;color:#9ca3af;font-weight:500;">ZIP</span>
                        <span style="font-size:11px;color:#374151;">${site.postal_code}</span>
                    </div>` : ''}
                </div>
                ${viewMode === 'solar' ? `
                <div style="margin-top:12px;padding-top:12px;border-top:1px solid #E5EDDB;">
                    <div style="display:inline-flex;align-items:center;gap:6px;background:${catStyle.tagBg};border:1px solid ${catStyle.border};border-radius:20px;padding:3px 10px;margin-bottom:10px;">
                        <span style="font-size:12px;font-weight:700;color:${catStyle.tagText};">${catStyle.label}</span>
                    </div>
                    ${solar && solar.irradiance != null ? `
                    <div style="display:flex;flex-direction:column;gap:5px;">
                        <div style="display:flex;justify-content:space-between;align-items:center;" title="This is the total solar energy received per square meter today">
                            <span style="font-size:11px;color:#9ca3af;font-weight:500;cursor:help;border-bottom:1px dotted #9ca3af;">☀️ Daily Irradiance <span style="font-size:9px;background:#e5e7eb;color:#6b7280;padding:0 4px;border-radius:4px;vertical-align:middle;">?</span></span>
                            <span style="font-size:11px;color:#D97706;font-weight:700;">${solar.irradiance} kWh/m²</span>
                        </div>
                        <div style="margin-bottom:4px;height:4px;background:#E5E7EB;border-radius:2px;overflow:hidden;">
                            <div style="width:${Math.min(100, (solar.score / 8) * 100)}%;height:100%;background:${catStyle.color};border-radius:2px;"></div>
                        </div>
                        <div style="display:flex;justify-content:space-between;align-items:center;">
                            <span style="font-size:11px;color:#9ca3af;font-weight:500;">🌡️ Avg Temp</span>
                            <span style="font-size:11px;color:#C2410C;font-weight:700;">${formatTemp(solar.temperature)}°F</span>
                        </div>
                        <div style="margin-top:8px;padding:8px;background:#F4FBF0;border-radius:8px;border-left:3px solid ${accentColor};">
                            <p style="font-size:10px;color:#6b7280;line-height:1.5;margin:0;font-style:italic;">${solar.advice}</p>
                    </div>` : `<div style="display:flex; align-items:center; gap: 6px;"><div style="width:12px;height:12px;border:2px solid #9ca3af;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;"></div><p style="font-size:11px;color:#9ca3af;font-style:italic;margin:0;">Fetching climate data...</p></div>`}
                </div>` : ''}
                ${viewMode === 'forecast' && forecast ? (() => {
            const d1 = calculateD1Score(forecast, spaceWeather);
            if (!d1) return '';
            accentColor = d1.color;
            return `
                    <div style="margin-top:12px;padding-top:12px;border-top:1px solid #E5EDDB;">
                        <div style="display:inline-flex;align-items:center;gap:6px;background:${d1.color}22;border:1px solid ${d1.color}66;border-radius:20px;padding:3px 10px;margin-bottom:10px;">
                            <span style="font-size:12px;font-weight:700;color:${d1.color};">Expected next 24 hour (D-1) prediction of the site</span>
                        </div>
                        <div style="display:flex;flex-direction:column;gap:5px;">
                            <div style="display:flex;justify-content:space-between;align-items:center;">
                                <span style="font-size:11px;color:#9ca3af;font-weight:500;">🌡️ 24h Avg Temp</span>
                                <span style="font-size:11px;color:#C2410C;font-weight:700;">${formatTemp(d1.avgTemp)}°F</span>
                            </div>
                            <div style="display:flex;justify-content:space-between;align-items:center;">
                                <span style="font-size:11px;color:#9ca3af;font-weight:500;">☀️ 24h Avg GHI</span>
                                <span style="font-size:11px;color:#D97706;font-weight:700;">${d1.avgIrradiance} W/m²</span>
                            </div>
                            <div style="display:flex;justify-content:space-between;align-items:center;">
                                <span style="font-size:11px;color:#9ca3af;font-weight:500;">📊 D-1 Index Score</span>
                                <span style="font-size:11px;color:${d1.color};font-weight:700;">${d1.score} / 100</span>
                            </div>
                            <div style="margin-top:8px;padding:8px;background:${d1.color}11;border-radius:8px;border-left:3px solid ${d1.color};">
                                <p style="font-size:10px;color:#6b7280;line-height:1.5;margin:0;font-style:italic;">${d1.advice}</p>
                            </div>
                        </div>
                    </div>`;
        })() : ''}
                ${forecastSection}
            </div>
        </div>
    `;
};

// ── Site name extractor ──────────────────────────────────────────────────────
const getSiteName = (s) => {
    const keys = Object.keys(s);
    const nameKey =
        keys.find(k => /^site_name$/i.test(k)) ||
        keys.find(k => /site location name/i.test(k)) ||
        keys.find(k => /site name/i.test(k)) ||
        keys.find(k => /location name/i.test(k)) ||
        keys.find(k => /^name$/i.test(k)) ||
        keys.find(k => /^location$/i.test(k)) ||
        keys.find(k => /^site$/i.test(k));
    return nameKey ? s[nameKey] : 'Unknown Location';
};

// ── Map Resizer (fixes grey tiles on sidebar toggle) ────────────────────
const MapResizer = ({ isPanelOpen }) => {
    const map = useMap();
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            map.invalidateSize();
        });
        
        const container = map.getContainer();
        if (container) {
            resizeObserver.observe(container);
        }
        
        return () => {
            if (container) {
                resizeObserver.unobserve(container);
            }
            resizeObserver.disconnect();
        };
    }, [map]);

    // Also trigger on isPanelOpen change with a small delay for CSS transitions
    useEffect(() => {
        const timer = setTimeout(() => map.invalidateSize(), 310);
        return () => clearTimeout(timer);
    }, [isPanelOpen, map]);
    
    return null;
};

// ── Map Events Listener ──────────────────────────────────────────────────────
const MapEventHandler = ({ onToggleSolar, onBoundsChange }) => {
    const map = useMapEvents({
        overlayadd: (e) => {
            if (e.name === '☀️ Solar Health') onToggleSolar && onToggleSolar(true);
        },
        overlayremove: (e) => {
            if (e.name === '☀️ Solar Health') onToggleSolar && onToggleSolar(false);
        },
        moveend: () => {
            if (onBoundsChange) onBoundsChange(map.getBounds().pad(0));
        },
        zoomend: () => {
            if (onBoundsChange) onBoundsChange(map.getBounds().pad(0));
        }
    });

    useEffect(() => {
        if (onBoundsChange && map) {
            setTimeout(() => {
                onBoundsChange(map.getBounds().pad(0));
            }, 0);
        }
    }, [map, onBoundsChange]);

    return null;
};

// ── Region Lock ──────────────────────────────────────────────────────────────
// ── Region Lock ──────────────────────────────────────────────────────────────
const GLOBAL_MAX_BOUNDS = [[-90, -180], [90, 180]];

const RegionLock = ({ regionBounds, siteBounds, regionView }) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;
        
        // Use a small timeout to allow the flex container to calculate its size properly.
        // This prevents the 'Cannot read properties of undefined (reading '_leaflet_pos')' error.
        const timer = setTimeout(() => {
            if (!map || !map.getContainer()) return; // Map was unmounted
            map.invalidateSize();
            
            // If there is no data at all, sit at the default center.
            if (!regionBounds && !siteBounds) {
                map.setMinZoom(2);
                map.setMaxBounds(null);
                map.setMaxBounds(GLOBAL_MAX_BOUNDS);
                map.flyTo([39.0, -96.0], 5, { duration: 1.2, animate: true });
                return;
            }

            map.setMaxBounds(null);
            map.setMinZoom(2);

            const onAnimEnd = () => {
                map.off('moveend', onAnimEnd);
                setTimeout(() => {
                    if (!map || !map.getContainer()) return;
                    map.setMaxBounds(GLOBAL_MAX_BOUNDS);
                    map.setMinZoom(2);
                }, 10);
            };

            map.on('moveend', onAnimEnd);

            if (regionBounds) {
                // Animate to strict Region framing
                if (regionView && regionView.center && regionView.zoom) {
                    map.flyTo(regionView.center, regionView.zoom, {
                        duration: 1.4, animate: true, easeLinearity: 0.25,
                    });
                } else {
                    // Width-first logic for Western region to hide oceans
                    const target = siteBounds || regionBounds;
                    const containerSize = map.getSize();
                    const boundsObj = L.latLngBounds(target);
                    const center = boundsObj.getCenter();

                    const refZoom = map.getMaxZoom();
                    const nw = map.project(boundsObj.getNorthWest(), refZoom);
                    const se = map.project(boundsObj.getSouthEast(), refZoom);

                    const boundsWidthPx = Math.abs(se.x - nw.x);
                    const boundsHeightPx = Math.abs(se.y - nw.y);

                    const hPad = Math.round(containerSize.x * 0.05);
                    const vPad = Math.round(containerSize.y * 0.05);
                    const availW = containerSize.x - hPad * 2;
                    const availH = containerSize.y - vPad * 2;

                    const widthZoom = refZoom + Math.log2(availW / boundsWidthPx);
                    const heightZoom = refZoom + Math.log2(availH / boundsHeightPx);

                    const targetZoom = Math.min(Math.max(widthZoom, heightZoom), 14);

                    map.flyTo(center, targetZoom, {
                        duration: 1.4, animate: true, easeLinearity: 0.25,
                    });
                }
            } else {
                // Dynamic filtered data (e.g. client selection outside regions)
                // Let Leaflet auto-adjust using fitBounds so all sites are visible.
                map.flyToBounds(siteBounds, {
                    duration: 1.4,
                    padding: [40, 40],
                    maxZoom: 12
                });
            }
        }, 50);

        // Cleanup
        return () => {
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionBounds, siteBounds, regionView]);

    return null;
};

// ── Dynamic Markers ──────────────────────────────────────────────────────────
const DynamicMarkers = ({ groupedData, viewMode, solarData, forecastData, spaceWeatherData, onSiteClick, timeOffset = 0, isEmbedded = false }) => {
    const map = useMap();
    // Track which site is currently "clicked open" so we can update its popup content
    const openSiteRef = useRef(null);    // { lat, lng, siteName, clientName, site }
    const openMarkerRef = useRef(null);  // Leaflet marker layer reference
    const hoverTimers = useRef(new Map()); // Store timeout IDs for hover-fetching

    const getSolarInfo = useCallback((lat, lng) => {
        if (!solarData || solarData.size === 0) return null;
        return solarData.get(`${parseFloat(lat).toFixed(4)},${parseFloat(lng).toFixed(4)}`) || null;
    }, [solarData]);

    const getForecastInfo = useCallback((lat, lng) => {
        if (!forecastData || forecastData.size === 0) return null;
        return forecastData.get(`${parseFloat(lat).toFixed(4)},${parseFloat(lng).toFixed(4)}`) || null;
    }, [forecastData]);

    // Reset style when a popup closes
    useEffect(() => {
        const onMapPopupClose = (e) => {
            // Only unhighlight if we are closing the popup of the currently tracked marker
            if (openMarkerRef.current && e.popup && e.popup._source === openMarkerRef.current) {
                try {
                    const cColor = (openSiteRef.current && openSiteRef.current.site)
                        ? (openSiteRef.current.site.Color || openSiteRef.current.site.color || '#76AB3F')
                        : '#76AB3F';
                    openMarkerRef.current.setStyle({
                        weight: 1.5,
                        fillOpacity: 0.85,
                        color: 'rgba(255,255,255,0.7)',
                        fillColor: cColor,
                    });
                } catch (err) { }
                openSiteRef.current = null;
                openMarkerRef.current = null;
            }
        };
        map.on('popupclose', onMapPopupClose);
        return () => { map.off('popupclose', onMapPopupClose); };
    }, [map]);

    return (
        <>
            {Object.entries(groupedData).map(([clientName, sites]) => {
                const markers = sites
                    .sort((a, b) => (parseFloat(b.size) || 0) - (parseFloat(a.size) || 0))
                    .map((site, index) => {
                                const lat = parseFloat(site.Latitude || site.latitude);
                                const lng = parseFloat(site.Longitude || site.longitude);
                                if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return null;

                                const solar = getSolarInfo(lat, lng);
                                const forecast = getForecastInfo(lat, lng);
                                const clientColor = site.Color || site.color || '#76AB3F';
                                const markerFillColor = clientColor;
                                let readingColor = clientColor;

                                if (viewMode !== 'client') {
                                    if (solar && viewMode === 'score') {
                                        readingColor = solar.color || clientColor;
                                    }

                                    if (viewMode !== 'client' && viewMode !== 'score') {
                                        if (forecast) {
                                            if (viewMode === 'forecast') {
                                                const d1Score = calculateD1Score(forecast, spaceWeatherData);
                                                if (d1Score) readingColor = d1Score.color;
                                            } else {
                                                const targetSec = getTargetSec(timeOffset);
                                                let fc = forecast.find(f => (typeof f.time === 'number' ? f.time : Math.floor(new Date(f.time).getTime() / 1000)) >= targetSec);
                                                if (!fc && forecast.length > 1) fc = forecast[1];
                                                if (fc) {
                                                    if (viewMode === 'wind') readingColor = getWindColor(fc.windSpeed);
                                                    else if (viewMode === 'wind_gust') readingColor = getWindGustColor(fc.windGust);
                                                    else if (viewMode === 'cloud') readingColor = getCloudColor(fc.cloudCover);
                                                    else if (viewMode === 'thunderstorm') readingColor = getThunderstormColor(fc.cape);
                                                    else if (viewMode === 'irradiance') readingColor = getIrradianceColor(fc.ghi);
                                                    else if (viewMode === 'temperature') readingColor = getTemperatureColor(fc.temperature);
                                                }
                                            }
                                        } else {
                                            readingColor = '#D1D5DB';
                                        }
                                    }
                                }

                                const size = 4;
                                const siteName = getSiteName(site);
                                const tooltipHtml = buildTooltipContent(siteName, clientName, lat, lng, site, viewMode, solar, forecast, spaceWeatherData, timeOffset);
                                const popupHtml = buildPopupContent(siteName, clientName, lat, lng, site, viewMode, solar, forecast, spaceWeatherData, timeOffset);
                                const isOpen = openSiteRef.current && openSiteRef.current.lat === lat && openSiteRef.current.lng === lng;
                                const initialRenderColor = isOpen && readingColor !== '#D1D5DB' ? readingColor : markerFillColor;

                                return (
                                    <CircleMarker
                                        key={`${clientName}-${index}-${timeOffset}`}
                                        center={[lat, lng]}
                                        pathOptions={{
                                            color: 'rgba(255,255,255,0.7)',
                                            fillColor: initialRenderColor,
                                            fillOpacity: 0.85,
                                            weight: 1.5,
                                            opacity: 1,
                                            className: 'radium-marker',
                                            interactive: true,
                                            bubblingMouseEvents: false,
                                        }}
                                        radius={size}
                                        eventHandlers={{
                                            click: (e) => {
                                                e.target.closeTooltip();
                                                openSiteRef.current = { lat, lng, siteName, clientName, site };
                                                openMarkerRef.current = e.target;

                                                e.target.setStyle({
                                                    weight: 3,
                                                    fillOpacity: 1,
                                                    color: readingColor,
                                                    fillColor: readingColor,
                                                });
                                                if (onSiteClick) onSiteClick(site);
                                            },
                                            mouseover: (e) => {
                                                if (openMarkerRef.current !== e.target) {
                                                    e.target.openTooltip();
                                                }
                                                e.target.setStyle({
                                                    weight: 3,
                                                    fillOpacity: 1,
                                                    color: readingColor,
                                                    fillColor: readingColor,
                                                });

                                                if (!forecast && viewMode !== 'client' && viewMode !== 'score' && onSiteClick) {
                                                    const siteKey = site.id || `${lat},${lng}`;
                                                    const timerId = setTimeout(() => {
                                                        onSiteClick(site);
                                                    }, 400);
                                                    hoverTimers.current.set(siteKey, timerId);
                                                }
                                            },
                                            mouseout: (e) => {
                                                e.target.closeTooltip();

                                                const siteKey = site.id || `${lat},${lng}`;
                                                if (hoverTimers.current.has(siteKey)) {
                                                    clearTimeout(hoverTimers.current.get(siteKey));
                                                    hoverTimers.current.delete(siteKey);
                                                }
                                                if (openMarkerRef.current !== e.target) {
                                                    e.target.setStyle({
                                                        weight: 1.5,
                                                        fillOpacity: 0.85,
                                                        color: 'rgba(255,255,255,0.7)',
                                                        fillColor: markerFillColor,
                                                    });
                                                }
                                            },
                                        }}
                                    >
                                        <Tooltip
                                            direction="auto"
                                            offset={[0, -8]}
                                            opacity={1}
                                            className="site-hover-tooltip"
                                        >
                                            <div dangerouslySetInnerHTML={{ __html: tooltipHtml }} />
                                        </Tooltip>
                                        <Popup
                                            closeButton={true}
                                            autoPan={true}
                                            autoPanPaddingTopLeft={[0, 70]}
                                            autoClose={true}
                                            closeOnClick={false}
                                            className="site-detail-popup"
                                        >
                                            <div dangerouslySetInnerHTML={{ __html: popupHtml }} />
                                        </Popup>
                                    </CircleMarker>
                                );
                            });

                const group = <LayerGroup key={isEmbedded ? clientName : undefined}>{markers}</LayerGroup>;

                if (isEmbedded) {
                    return group;
                }

                return (
                    <LayersControl.Overlay checked key={clientName} name={`${clientName}`}>
                        {group}
                    </LayersControl.Overlay>
                );
            })}
        </>
    );
};

const MapView = ({ data, selectedClient, viewMode = 'client', solarData = new Map(), forecastData = new Map(), spaceWeatherData, timeOffset = 0, onToggleSolar, onBoundsChange, onSiteClick, isPanelOpen, activeRegion, regionBounds, regionView, isEmbedded }) => {
    const defaultCenter = [39.0, -96.0];
    const defaultZoom = 5;
    const [showWind, setShowWind] = useState(true);
    const [windColorScale, setWindColorScale] = useState('ocean');

    const groupedData = useMemo(() => data.reduce((acc, site) => {
        const client = (site.client_name || 'General').trim();
        if (!acc[client]) acc[client] = [];
        acc[client].push(site);
        return acc;
    }, {}), [data]);

    const siteBounds = useMemo(() => {
        if (!data || data.length === 0) return null;
        if (!activeRegion && selectedClient === 'All Clients') return null;
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
        data.forEach(site => {
            const lat = parseFloat(site.Latitude || site.latitude);
            const lng = parseFloat(site.Longitude || site.longitude);
            if (!isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)) {
                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
                if (lng < minLng) minLng = lng;
                if (lng > maxLng) maxLng = lng;
            }
        });
        if (minLat === Infinity) return null;
        // 12% padding so dots aren't clipped near edges / UI overlays
        const latPad = (maxLat - minLat) * 0.12;
        const lngPad = (maxLng - minLng) * 0.12;
        return [[minLat - latPad, minLng - lngPad], [maxLat + latPad, maxLng + lngPad]];
    }, [data, activeRegion, selectedClient]);



    return (
        <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            minZoom={2}
            zoomControl={false} // Disable default zoom control
            attributionControl={false} // Disable attribution text
            preferCanvas={true}
            maxBounds={[[-90, -180], [90, 180]]}
            maxBoundsViscosity={1.0}
            style={{ height: '100%', width: '100%' }}
        >
            {/* Map filters are now allowed to show in embedded mode as well */}
            <CustomZoomSlider 
                showWind={showWind} 
                setShowWind={setShowWind} 
                windColorScale={windColorScale} 
                setWindColorScale={setWindColorScale} 
                isEmbedded={isEmbedded}
            />
            <MapResizer isPanelOpen={isPanelOpen} />
            <MapEventHandler onToggleSolar={onToggleSolar} onBoundsChange={onBoundsChange} />
            <RegionLock regionBounds={regionBounds} siteBounds={siteBounds} regionView={regionView} />
            <LayersControl position="bottomright">
                {/* Base layers */}

                {/* Satellite + Labels: Esri imagery with Esri Boundaries & Places overlay
                    — shows country/state names and borders, NO green road lines */}
                <LayersControl.BaseLayer name="Satellite">
                    <LayerGroup>
                        <TileLayer
                            attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        />
                        <TileLayer
                            attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
                            url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                            opacity={1}
                        />
                    </LayerGroup>
                </LayersControl.BaseLayer>

                {/* Satellite (Old) — Esri imagery + CartoDB light labels */}
                <LayersControl.BaseLayer name="Satellite (Old)">
                    <LayerGroup>
                        <TileLayer
                            attribution='Tiles &copy; Esri'
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        />
                        <TileLayer
                            attribution='&copy; <a href="https://carto.com">CARTO</a>'
                            url="https://{s}.basemaps.cartocdn.com/rastertiles/light_only_labels/{z}/{x}/{y}{r}.png"
                        />
                    </LayerGroup>
                </LayersControl.BaseLayer>

                <LayersControl.BaseLayer checked name="OpenStreetMap">
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        noWrap={true}
                    />
                </LayersControl.BaseLayer>

                <LayersControl.BaseLayer name="Light">
                    <TileLayer
                        attribution='&copy; <a href="https://carto.com">CARTO</a>'
                        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                        noWrap={true}
                    />
                </LayersControl.BaseLayer>

                <LayersControl.BaseLayer name="Terrain">
                    <TileLayer
                        attribution='Tiles &copy; Esri'
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
                    />
                </LayersControl.BaseLayer>

                {viewMode === 'snow' && (
                    <TileLayer
                        attribution='&copy; Tomorrow.io'
                        url={`https://api.tomorrow.io/v4/map/tile/{z}/{x}/{y}/snowAccumulation/now.png?apikey=${process.env.REACT_APP_TOMORROW_IO_API_KEY}`}
                        opacity={0.7}
                        maxNativeZoom={6}
                    />
                )}
                {showWind && <WindLayer 
                    key={`wind-${windColorScale}`} 
                    data={windData} 
                    colorScale={
                        windColorScale === 'ocean' ? OCEAN_WIND_SCALE : 
                        windColorScale === 'white' ? WHITE_WIND_SCALE : 
                        CLASSIC_WIND_SCALE
                    } 
                />}

                {viewMode === 'score' && (
                    <LayerGroup>
                        <SolarHeatmap sites={data} solarData={solarData} />
                    </LayerGroup>
                )}
                {!isEmbedded && (
                    <DynamicMarkers
                        groupedData={groupedData}
                        viewMode={viewMode}
                        solarData={solarData}
                        forecastData={forecastData}
                        spaceWeatherData={spaceWeatherData}
                        onSiteClick={onSiteClick}
                        timeOffset={timeOffset}
                        isEmbedded={false}
                    />
                )}
            </LayersControl>
            {isEmbedded && (
                <DynamicMarkers
                    groupedData={groupedData}
                    viewMode={viewMode}
                    solarData={solarData}
                    forecastData={forecastData}
                    spaceWeatherData={spaceWeatherData}
                    onSiteClick={onSiteClick}
                    timeOffset={timeOffset}
                    isEmbedded={true}
                />
            )}
            {viewMode === 'solar' && <SolarLegend />}
            {['temperature', 'wind', 'wind_gust', 'thunderstorm', 'irradiance', 'score'].includes(viewMode) && (
                <MapLegend viewMode={viewMode} />
            )}


        </MapContainer>
    );
};

export default MapView;
